import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Button,
  DialogProps,
  Divider,
  FormControlLabel,
  Checkbox as CheckboxMUI,
  Grid,
  Icon,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import * as Yup from "yup";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

//components
import { RefferalTypes, RepType } from "../../../constants/MenuSelection";
import useAuth from "../../../hooks/useAuth";
import useCases from "../../../hooks/useCases";
import useContacts from "../../../hooks/useContacts";
import useDocuSign from "../../../hooks/useDocuSign";
import useUser from "../../../hooks/useUser";
import { contactTypes } from "../../../types/contactTypes";
import { isValidateNextParoleDateFormat } from "../../../utils/isValidateNextParoleDateFormat";
import { isValidateParoleEligibilityDate } from "../../../utils/isValidateParoleDateFormat";
import { useSnackbarHelper } from "../../components/Snackbar";
import BasicAutocomplete from "../../components/inputs/BasicAutocomplete";
import BasicDatePicker from "../../components/inputs/BasicDatePicker";
import Checkbox from "../../components/inputs/CheckBox";
import MuiTextField from "../../components/inputs/MuiTextField";
import NumberField from "../../components/inputs/NumberField";
import BasicModal from "../../components/modal/BasicModal";
import TableSkeleton from "../../components/skeleton/TableSkeleton";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Select from "../../components/inputs/Select";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { uploadImageToS3 } from "../../components/common/uploadImages";
import { stripS3Prefix } from "../../../utils/s3";
import { useModal } from "mui-modal-provider";
import SelectWorkflowModal from "./SelectWorkflowModal";
import { IconEdit } from "@tabler/icons-react";

dayjs.extend(isSameOrBefore); // Enable the plugin

interface Props extends DialogProps {
  onClose: () => void;
  data?: contactTypes;
}

function AddNewCaseModal({ onClose, data, ...props }: Props) {
  const { SendDocuSignMail, isLoadingDocuSignMail } = useDocuSign();

  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null); // Ref for the hidden file input
  const [file, setFile] = useState<File | undefined>();
  const {
    // inmates,
    inmatesAll,
    isContactsLoading,
  } = useContacts(undefined, undefined, undefined, true);

  const { request, userId } = useAuth();
  const { addCase, isAddCaseLoading, boardMembers } = useCases();
  const [isTriggerWorkflow, setIsTriggerWorkflow] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);

  const { repTypes } = useUser(userId);
  const { lawyers, staff } = useUser(
    undefined,
    undefined,
    undefined,
    undefined,
    true,
  );

  const showSnackbar = useSnackbarHelper();
  const { showModal } = useModal();

  const members = boardMembers?.map((option: any) => {
    return { label: option?.name, value: option?.name };
  });
  const initialValues = useMemo(
    () => ({
      contactId: null,
      paroleEligibilityDate: null,
      nextParoleEligibilityDate: null,
      caseStatus: "active",
      repType: "",
      feeQuoted: "",
      initialContactDate: null,
      followUpDate: null,
      retentionDate: null,
      mailType: "",
      // phoneType: "",
      lawyer: null,
      isAllLawyer: true,
      secondaryLawyer: null,
      staff: null,
      isAllStaff: true,
      leadVoter: "",
      referralType: "",
      isExtraordinaryVoteCase: false,
      isExistingClient: false,
      referralDetails: "",
      paroleHiringDateTime: null,
      triggerWorkflow: false,
      priorPayment: [{ type: "", paidDate: null, amount: "" }],
      emailBody: "",
    }),
    [],
  );

  const priorPaymentSchema = Yup.object({
    type: Yup.string().required("Required"),
    paidDate: Yup.date().required("Required"),
    amount: Yup.string().required("Required"),
  });

  const validationSchema: any = useMemo(
    () =>
      Yup.object({
        contactId: Yup.object().required("Required"),
        // paroleEligibilityDate: Yup.date().nullable(),
        paroleEligibilityDate: Yup.string().when(
          "contactId",
          (contactId: any, schema: any) => {
            return !contactId?.[0]?.isCountyInmate
              ? schema.nullable()
              : schema.required("Required");
          },
        ),

        nextParoleEligibilityDate: Yup.string().nullable(),
        caseStatus: Yup.string().required("Required"),
        repType: Yup.string().required("Required"),
        feeQuoted: Yup.string(),
        initialContactDate: Yup.date().nullable(),
        followUpDate: Yup.date().nullable(),
        retentionDate: Yup.date().nullable(),
        mailType: Yup.string().required("Required"),
        // phoneType: Yup.string().required("Required"),
        // lawyer: Yup.string().when(
        //   "caseStatus",
        //   (caseStatus: any, schema: any) => {
        //     return caseStatus?.[0] !== "active"
        //       ? schema.nullable()
        //       : schema.required("Required");
        //   }
        // ),
        isAllLawyer: Yup.boolean(),
        isAllStaff: Yup.boolean(),
        // lawyer: Yup.string().required("Required"),
        // staff: Yup.string().required("Required"),
        secondaryLawyer: Yup.string().nullable(),

        lawyer: Yup.string().when(
          "isAllLawyer",
          (isAllLawyer: any, schema: any) => {
            return isAllLawyer?.[0]
              ? schema.nullable()
              : schema.required("Required");
          },
        ),
        staff: Yup.string().when(
          "isAllStaff",
          (isAllStaff: any, schema: any) => {
            return isAllStaff?.[0]
              ? schema.nullable()
              : schema.required("Required");
          },
        ),

        leadVoter: Yup.string().notRequired(),
        referralType: Yup.string().nullable(),
        isExtraordinaryVoteCase: Yup.boolean(),
        isExistingClient: Yup.boolean(),
        referralDetails: Yup.string(),
        paroleHiringDateTime: Yup.date().nullable(),
        triggerWorkflow: Yup.boolean(),
        priorPayment: Yup.array().when(
          "isExistingClient",
          (isExistingClient, schema) => {
            return isExistingClient?.[0]
              ? schema.of(priorPaymentSchema).required("Required")
              : schema.notRequired();
          },
        ),
        emailBody: Yup.string().nullable(),
      }),

    //eslint-disable-next-line
    [],
  );

  const formContext = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const { watch, setValue } = formContext;
  // const caseStatus = watch("caseStatus");
  const repType = watch("repType");

  const contact: any = watch("contactId");
  // const lawyer = watch("lawyer");
  const isAllLawyer = watch("isAllLawyer");
  const isAllStaff = watch("isAllStaff");
  const secondaryLawyer = watch("secondaryLawyer");
  const isExistingClient = watch("isExistingClient");
  const paroleEligibilityDate = watch("paroleEligibilityDate");
  // const triggerWorkflow = watch('triggerWorkflow')
  // const priorPayment = watch("priorPayment");

  useEffect(() => {
    if (isAllStaff) {
      setValue("staff", null);
    }
    if (isAllLawyer) {
      setValue("lawyer", null);
    }
    if (!isExistingClient) {
      setValue("triggerWorkflow", false);
      setValue("priorPayment", [{ type: "", paidDate: null, amount: "" }]);
      setFile(undefined);
      setIsTriggerWorkflow(false);
    }
    if (contact?.isCountyInmate) {
      setValue("triggerWorkflow", false);
      setIsTriggerWorkflow(false);
    }

    //eslint-disable-next-line
  }, [isAllLawyer, isAllStaff, isExistingClient, contact]);

  useEffect(() => {
    if (contact && !isExistingClient) {
      const capitalizeStr = (str: string) =>
        str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

      const contactFirstName = capitalizeStr(contact?.firstName) || "";
      let inmateNameStr = contact?.isCountyInmate
        ? contact?.inmate?.name || "County Inmate"
        : contact?.inmate?.name;
      const userFirstName = contact?.createdBy?.firstName || "";
      const userLastName = contact?.createdBy?.lastName || "";
      const userName = `${userFirstName} ${userLastName}`;
      let inmateFirstName = "";
      if (inmateNameStr) {
        if (inmateNameStr.includes(",")) {
          // Format: LAST, FIRST MIDDLE
          inmateFirstName =
            capitalizeStr(inmateNameStr.split(",")[1]?.trim()?.split(" ")[0]) ||
            "";
        } else {
          inmateFirstName = capitalizeStr(inmateNameStr.split(" ")[0]) || "";
        }
      }

      const defaultText = `Hi ${contactFirstName},

Thank you for taking the time to speak with me. To get started on ${inmateFirstName}’s case, please review and electronically sign your representation agreement and make your payment—or at least the $500 down payment—here:

{payment-link}

Once we receive both, we will immediately begin the next steps for ${inmateFirstName}, including the questionnaire, TDCJ records request, certificate collection, and guidance on how to make the most of the time before parole eligibility.

We appreciate the opportunity to help.

${userName}
Cox Law Parole`;

      setValue("emailBody", defaultText);
    } else {
      setValue("emailBody", "");
    }
    //eslint-disable-next-line
  }, [contact, isExistingClient, setValue]);

  useEffect(() => {
    if (!isTriggerWorkflow) {
      setSelectedTaskIds([]);
    }
  }, [isTriggerWorkflow]);

  useEffect(() => {
    //validate and set parole eligibility date
    if (
      isValidateParoleEligibilityDate(contact?.inmate?.paroleEligibilityDate)
    ) {
      setValue("paroleEligibilityDate", contact?.inmate?.paroleEligibilityDate);
    } else if (
      !contact?.isCountyInmate &&
      !isValidateParoleEligibilityDate(contact?.inmate?.paroleEligibilityDate)
    ) {
      //(case without Inmate)
      setValue("paroleEligibilityDate", null);
    }

    //Set the value to null if isCountyInmate is true and the date is before today (case without Inmate).
    if (
      dayjs(paroleEligibilityDate).isSameOrBefore(dayjs(), "day") &&
      contact?.isCountyInmate
    ) {
      setValue("paroleEligibilityDate", null);
    }

    //validate and set parole eligibility date
    if (
      isValidateNextParoleDateFormat(
        contact?.inmate?.paroleReviewInfo?.nextParoleDate,
      )
    ) {
      setValue(
        "nextParoleEligibilityDate",
        contact?.inmate?.paroleReviewInfo?.nextParoleDate,
      );
    } else {
      setValue("nextParoleEligibilityDate", null);
    }

    //eslint-disable-next-line
  }, [contact, setValue]);

  useEffect(() => {
    //set feeQuoted according to fee type
    const matchingRepType = repTypes?.find(
      (rep: any) => rep?.type?.toLowerCase() === repType?.toLowerCase(),
    );
    if (matchingRepType) {
      setValue("feeQuoted", matchingRepType?.value);
    }
    if (contact && !contact?.isCountyInmate) {
      // If paroleEligibilityDate is not available, use nextParoleDate as paroleEligibilityDate
      if (contact?.inmate?.paroleEligibilityDate === "NOT AVAILABLE") {
        const nextParoleDate =
          contact?.inmate?.paroleReviewInfo?.nextParoleDate;

        if (nextParoleDate) {
          const [month, year] = nextParoleDate.split("/");

          if (month && year) {
            const formattedDate = `${year}-${month.padStart(2, "0")}-01`;
            setValue("paroleEligibilityDate", new Date(formattedDate) as any);
          }
        }
      } else {
        setValue(
          "paroleEligibilityDate",
          contact?.inmate?.paroleEligibilityDate,
        );
      }
    }
  }, [repType, setValue, contact, repTypes]);

  // useEffect(() => {
  //   if (lawyer) {
  //     setValue("caseStatus", "active");
  //   }
  // }, [lawyer, setValue]);

  const handleDocuSignMail = async (values: any) => {
    try {
      const data = await SendDocuSignMail(values);
      // const message = data?.message || "Success";
      // showSnackbar(message, "success");
      return data;
    } catch (error: any) {
      const message = error?.response?.data?.message || "An error occurred";
      showSnackbar(message, "error");
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Enforce maximum file size of 5MB
      if (selectedFile.size > 5 * 1024 * 1024) {
        // clear the input so the user can re-select
        event.target.value = "";
        showSnackbar("File size should be less than 5MB", "error");
        return;
      }

      setFile(selectedFile);
    }
  };

  function shortenFileName(fileName: string, maxLength: number = 20): string {
    const extensionIndex = fileName.lastIndexOf(".");

    // Handle files without extensions
    if (extensionIndex === -1 || fileName.length <= maxLength) {
      return fileName; // Return original if it's shorter than maxLength
    }

    const name = fileName.substring(0, extensionIndex); // File name without extension
    const extension = fileName.substring(extensionIndex); // File extension

    if (fileName.length <= maxLength) {
      return fileName;
    }

    // Start part (first N characters) and end part (last N characters of name)
    const startPart = name.substring(0, 15);
    const endPart = name.substring(name.length - 5);

    return `${startPart}..${endPart}${extension}`;
  }

  const openSelectWorkflowModal = useCallback(
    (shouldOpen: boolean, tasks: any) => {
      if (shouldOpen) {
        const modal: any = showModal(SelectWorkflowModal, {
          onClose: () => {
            modal.hide();
          },
          onAdd: (selectedTasks: any[]) => {
            // console.log("Received in parent:", selectedTasks); // ✅ handle in parent
            setSelectedTaskIds(selectedTasks);
            modal.hide();
          },
          data: shouldOpen,
          previousSelectedTasks: tasks,
        });
      }
    },
    //eslint-disable-next-line
    [],
  );

  const handleSubmit = async (values: any) => {
    values.followUpDate = values?.followUpDate ? [values.followUpDate] : null;
    const { contactId, mailType, isExistingClient, priorPayment, feeQuoted } =
      values;

    const inmateId = contactId?.inmate?._id;

    const contactName = contactId?.firstName + " " + contactId?.lastName;
    const inmateName = contactId?.inmate?.name || "";
    const tdcjNumber = contactId?.isCountyInmate
      ? contactId?._id
      : contactId?.inmate?.tdcjNumber;
    const sidNumber = contactId?.isCountyInmate
      ? contactId?._id
      : contactId?.inmate?.sidNumber;

    const isCountyInmate = contactId?.isCountyInmate;
    const contactMail = contactId?.email?.find(
      (e: any) => e?.emailType === mailType,
    )?.email;

    const selectedTasks = selectedTaskIds?.map((t: any) => t?._id);

    // const contactPhone = contactId?.phone?.find(
    //   (e: any) => e?.phoneType === phoneType
    // )?.phone;

    let totalPriorPayment = 0;
    if (isExistingClient) {
      totalPriorPayment = priorPayment
        ?.map((payment: any) => Number(payment?.amount) || 0)
        .reduce((sum: any, amount: any) => sum + amount, 0);
      if (totalPriorPayment > feeQuoted) {
        showSnackbar("Prior payment cannot exceed the quoted fee.", "error");
        return; // Prevents form submission
      }
      values.feeQuoted = (feeQuoted - totalPriorPayment).toString();
      values.priorPayment = priorPayment;
    } else {
      values.priorPayment = [];
    }
    const repType = values?.repType;
    try {
      // If the client is existing, not send the agreement
      let { envelopeId, docuSignUrl } = !isExistingClient
        ? (await handleDocuSignMail({
            contactName,
            contactMail,
            // contactPhone,
            inmateName: inmateName,
            tdcjNumber,
            sidNumber,
            amount: values.feeQuoted,
            repType,
            contactDetails: contactId,
            isCountyInmate,
            emailBody: values?.emailBody,
          })) || {}
        : { envelopeId: null, docuSignUrl: null };

      if (isExistingClient) {
        const folderName = "document";
        const fileData = {
          file: file,
          userId: userId,
          folderName: folderName,
          request,
        };
        let res: any;

        try {
          if (file) {
            setIsLoading(true);
            res = await uploadImageToS3(fileData);
            if (res?.Location) {
              docuSignUrl = stripS3Prefix(res.Location); // Assign the uploaded file URL
              setIsLoading(false);
            }
          }
        } catch (error: any) {
          setIsLoading(false);
          showSnackbar(error?.message || "Upload failed", "error");
        }
      }
      if (envelopeId || (isExistingClient && envelopeId === null)) {
        // create new case
        await addCase({
          ...values,
          contactId: contactId?._id,
          inmateId,
          envelopeId,
          docuSignUrl,
          isCountyInmate,
          isExistingClient,
          triggerWorkflow: isTriggerWorkflow,
          existingClientCompletedTasks: selectedTasks,
          // priorPayment,
          // totalPriorPayment,
        });

        const message = !isExistingClient
          ? "The case has been added, and the agreement has been sent to support contact."
          : "Case added successfully.";
        showSnackbar(message, "success");
        onClose();
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "An error occurred";

      showSnackbar(message, "error");
    }
  };

  const capitalizeFirstLetter = (string: string) => {
    if (string) {
      return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    }
  };
  const mailTypes = useMemo(() => {
    if (!contact?.email) return [];
    const array = contact.email.map((item: any) => {
      return {
        label: capitalizeFirstLetter(item?.emailType),
        value: item?.emailType,
      };
    });
    return array;
  }, [contact]);

  // const phoneTypes = useMemo(() => {
  //   if (!contact?.phone) return [];
  //   const array = contact.phone.map((item: any) => {
  //     return {
  //       label: capitalizeFirstLetter(item?.phoneType),
  //       value: item?.phoneType,
  //     };
  //   });
  //   return array;
  // }, [contact]);

  if (isContactsLoading) {
    return <TableSkeleton />;
  }

  return (
    <BasicModal
      formContext={formContext}
      onClose={onClose}
      onSubmit={handleSubmit}
      isLoader={isAddCaseLoading || isLoadingDocuSignMail || isLoading}
      loadingTitle="Loading..."
      title={"Add Case"}
      height="calc(100vh - 230px)"
      size="sm"
      content={
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
          <Grid item xs={6}>
            <BasicAutocomplete
              label="Contacts"
              placeholder="Search contacts"
              name="contactId"
              required
              options={inmatesAll}
              getOptionLabel={(option: any) =>
                `${option?.firstName} ${option?.lastName}`
              }
            />
          </Grid>

          <Grid item sm={6}>
            <BasicAutocomplete
              label="Mail Type"
              name="mailType"
              options={mailTypes}
              required
              getOptionValue={(option: any) => option?.value}
            />
          </Grid>
          {contact?.isCountyInmate && (
            <Grid item sm={12}>
              <Typography color={"#d32f2f"}>
                Note: You are creating a case with a County Inmate.
              </Typography>
            </Grid>
          )}
          {/* <Grid item sm={6}>
            <BasicAutocomplete
              label="Phone Type"
              name="phoneType"
              options={phoneTypes}
              required
              getOptionValue={(option: any) => option?.value}
            />
          </Grid> */}
          {/* <Grid item xs={6}>
            <BasicAutocomplete
              label="Case Status"
              name="caseStatus"
              required
              options={AddCaseFileStatus}
              getOptionValue={(option: any) => option?.value}
            />
          </Grid> */}
          <Grid item xs={6}>
            <BasicDatePicker
              label="Parole Eligibility Date"
              name="paroleEligibilityDate"
              disabled={!contact?.isCountyInmate}
              required={contact?.isCountyInmate}
              minDate={
                contact?.isCountyInmate
                  ? dayjs(new Date())
                  : dayjs("2018-01-01")
              }
            />
          </Grid>
          <Grid item xs={6}>
            <MuiTextField
              label="Next Parole Eligibility Date"
              name="nextParoleEligibilityDate"
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            {" "}
            <BasicAutocomplete
              label="Rep Type"
              name="repType"
              required
              options={RepType}
              getOptionValue={(option: any) => option?.value}
            />
          </Grid>
          <Grid item sm={6}>
            <NumberField
              label="Fee Quoted"
              name="feeQuoted"
              prefix="$ "
              decimalScale={2}
              fixedDecimalScale={true}
              max={99999999}
            />
          </Grid>
          <Grid item xs={6}>
            <BasicDatePicker
              label="Initial Contact Date"
              name="initialContactDate"
            />
          </Grid>
          <Grid item xs={6}>
            <BasicDatePicker label="Follow Up Date" name="followUpDate" />
          </Grid>
          <Grid item sm={6}>
            <BasicDatePicker label="Retention Date" name="retentionDate" />
          </Grid>

          <Grid item xs={6}>
            <BasicDatePicker label="Hiring Date" name="paroleHiringDateTime" />
          </Grid>
          {/* <Grid item sm={6}>
            <BasicAutocomplete
              label="Lawyer"
              name="lawyer"
              options={lawyers
                ?.filter((user: any) => user?._id !== secondaryLawyer)
                ?.map((user: any) => ({
                  label: `${user.firstName} ${user.lastName}`,
                  value: user?._id,
                }))}
              getOptionValue={(option: any) => option?.value}
              required
            />
          </Grid> */}
          <Grid item sm={6}>
            {/* <BasicAutocomplete
              label="Secondary Lawyer"
              name="secondaryLawyer"
              options={lawyers
                ?.filter((user: any) => user?._id !== lawyer)
                ?.map((user: any) => ({
                  label: `${user.firstName} ${user.lastName}`,
                  value: user?._id,
                }))}
              getOptionValue={(option: any) => option?.value}
            /> */}
            <Checkbox name="isAllLawyer" label="All Lawyers" />
          </Grid>
          <Grid item sm={5}>
            <Checkbox name="isAllStaff" label="All Staff" />
          </Grid>
          <Grid item xs={1}>
            <Tooltip
              title={
                "If you check the 'All Lawyer' checkbox, the ED will be considered the main lawyer. If you check the 'All Staff' checkbox, Jack will be considered the main staff."
              }
            >
              <Icon>
                <InfoOutlinedIcon
                  sx={{ fontSize: 20, color: "#008EBB", mt: 0.5 }}
                />
              </Icon>
            </Tooltip>
          </Grid>
          <Grid item sm={6}>
            <BasicAutocomplete
              label="Lawyer"
              name="lawyer"
              options={lawyers
                ?.filter((user: any) => user?._id !== secondaryLawyer)
                ?.map((user: any) => ({
                  label: `${user.firstName} ${user.lastName}`,
                  value: user?._id,
                }))}
              getOptionValue={(option: any) => option?.value}
              required={!isAllLawyer}
              disabled={isAllLawyer}
            />
          </Grid>
          <Grid item xs={6}>
            <BasicAutocomplete
              label="Staff"
              name="staff"
              required={!isAllStaff}
              options={staff?.map((user: any) => ({
                label: `${user.firstName} ${user.lastName}`,
                value: user?._id,
              }))}
              getOptionValue={(option: any) => option?.value}
              disabled={isAllStaff}
            />
          </Grid>

          <Grid item xs={12}>
            <BasicAutocomplete
              label="Lead Voter"
              name="leadVoter"
              options={members}
              getOptionValue={(option: any) => option?.value}
            />
          </Grid>
          <Grid item xs={6}>
            <Checkbox
              name="isExtraordinaryVoteCase"
              label="Extraordinary Vote Case?"
            />
          </Grid>
          <Grid item xs={6}>
            <Checkbox
              name="isExistingClient"
              label="Is Existing Client?"
              // labelPlacement="end"
            />
          </Grid>

          {isExistingClient && <SupportContact />}
          {isExistingClient && (
            <>
              <Grid item xs={5}>
                <Stack>
                  {" "}
                  {file && (
                    <Typography>{shortenFileName(file.name)}</Typography>
                  )}
                </Stack>
                <Stack spacing={2}>
                  {/* Align button at the end */}
                  <Button
                    variant="contained"
                    component="span"
                    sx={{ textTransform: "none", mb: 1 }}
                    onClick={() => {
                      fileInputRef.current?.click();
                    }} // Trigger the file input
                    startIcon={<UploadIcon />}
                  >
                    Upload Rep Agreement
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef} // Hidden file input
                    accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    style={{ display: "none" }} // Hide the file input element
                    onChange={handleFileSelect}
                  />
                </Stack>
              </Grid>
              {!contact?.isCountyInmate && (
                <>
                  <Grid item xs={6} mb={file ? "-25px" : "0"}>
                    {/* <Checkbox
                      name="triggerWorkflow"
                      label="Trigger workflows now?"
                      disabled={!file}
                      // onChange={(e: any) => console.log(e.target.value)}
                      // labelPlacement="end"
                    /> */}

                    <FormControlLabel
                      control={
                        <CheckboxMUI
                          name="triggerWorkflow"
                          value={isTriggerWorkflow}
                          disabled={!file}
                          onChange={(e: any) => {
                            openSelectWorkflowModal(
                              e.target.checked,
                              selectedTaskIds,
                            );
                            setIsTriggerWorkflow(e.target.checked);
                          }}
                          inputProps={{
                            "aria-label": "Trigger workflows now?",
                          }}
                        />
                      }
                      labelPlacement="start"
                      label="Trigger workflows now?"
                    />
                  </Grid>

                  <Grid item xs={1} mb={file ? "-25px" : "0"}>
                    <Tooltip
                      title={
                        "Do you want to trigger the workflow now, or wait until the first scheduled payment is made? If you want to trigger it now, please check the box.\nNote: You can only enable this checkbox after uploading a Representation Agreement."
                      }
                      PopperProps={{
                        style: { whiteSpace: "pre-line" },
                      }}
                    >
                      <Icon>
                        <InfoOutlinedIcon
                          sx={{ fontSize: 20, color: "#008EBB", mt: 0.5 }}
                        />
                      </Icon>
                    </Tooltip>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>

              {isTriggerWorkflow && (
                <>
                  <Grid item xs={6} mt={-1} mb={-1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Task(s) Already Completed
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    mt={-1}
                    mb={-1}
                    display={"flex"}
                    justifyContent={"flex-end"}
                  >
                    <Tooltip
                      title="Edit"
                      // placement="top"
                    >
                      <IconButton
                        onClick={() =>
                          openSelectWorkflowModal(true, selectedTaskIds)
                        }
                      >
                        <IconEdit style={{ cursor: "pointer" }} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12}>
                    {selectedTaskIds?.length > 0 ? (
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {selectedTaskIds.map((t: any) => (
                          <Chip
                            key={t._id}
                            label={t.task}
                            variant="outlined"
                            color="primary"
                            onDelete={() =>
                              setSelectedTaskIds((prev: any) =>
                                prev.filter((task: any) => task._id !== t._id),
                              )
                            }
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No tasks selected.
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                </>
              )}
            </>
          )}
          <Grid item xs={12}>
            <BasicAutocomplete
              label="Referral Type"
              name="referralType"
              options={RefferalTypes}
              getOptionValue={(option: any) => option?.value}
            />
          </Grid>
          <Grid item xs={12}>
            <MuiTextField
              minRows={2}
              multiline
              label="Referral Details"
              // placeholder="Referral Details"
              name="referralDetails"
              sx={{ borderColor: "#B2BAC2" }}
              // options={Task}
              //
            />
          </Grid>
          {!isExistingClient && (
            <Grid item xs={12}>
              <MuiTextField
                minRows={2}
                multiline
                label="DocuSign Email Body"
                // placeholder="Referral Details"
                name="emailBody"
                sx={{ borderColor: "#B2BAC2" }}
                // options={Task}
                //
              />
            </Grid>
          )}
          {/* <Grid item xs={6}>
            <CustomDateTime
              label="Parole Hiring Date&Time"
              name="paroleHiringDateTime"
            />
          </Grid>
          <Grid item xs={6}>
            <BasicAutoComplete
              multiple
              label="Parole Vote Result"
              name="paroleVoteResult"
              options={PayroleVoteResult}
              getOptionValue={(option: any) => option?.value}
            />
          </Grid> */}
        </Grid>
      }
      actions={
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
          <Grid item xs={7}>
            {!inmatesAll?.length && (
              <Alert
                sx={{
                  height: 35,
                  pt: 0,
                  pb: 0,
                  overflow: "hidden",
                  lineHeight: 1,
                  alignItems: "center",
                  backgroundColor: "transparent", // Remove background color
                  boxShadow: "none", // Remove box shadow
                  border: "none", // Remove border
                  color: "#d32f2f",
                }}
                severity="error"
              >
                Add/associate a contact with its inmate.
              </Alert>
            )}
          </Grid>
          <Grid item xs={5} textAlign={"right"}>
            <Button
              color="error"
              variant="outlined"
              onClick={onClose}
              disabled={
                isAddCaseLoading ||
                isLoadingDocuSignMail ||
                !inmatesAll?.length ||
                isLoading
              }
              sx={{ textTransform: "capitalize", mr: 4 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              sx={{ textTransform: "capitalize" }}
              disabled={
                isAddCaseLoading ||
                isLoadingDocuSignMail ||
                !inmatesAll?.length ||
                isLoading
              }
              type="submit"
            >
              Save
            </Button>
          </Grid>
        </Grid>
      }
      {...props}
    />
  );
}

export default AddNewCaseModal;

const SupportContact: React.FC = () => {
  const { control } = useFormContext();

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    control,
    name: "priorPayment",
  });

  const RenderEmailsItem = ({ i, arrayLength }: any) => {
    return (
      <>
        <Grid item xs={3}>
          <Select
            label="Type"
            name={`priorPayment.${i}.type`}
            required
            options={[
              { label: "Check", value: "cheque" },
              { label: "Cash", value: "cash" },
              { label: "Law pay", value: "lawpay" },
            ]}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <BasicDatePicker
            label="Paid Date"
            name={`priorPayment.${i}.paidDate`}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <NumberField
            label="Amount"
            name={`priorPayment.${i}.amount`}
            prefix="$ "
            max={999999999999999}
            required
            // decimalScale={2}
            // fixedDecimalScale={true}
          />
        </Grid>

        <Grid item xs={1}>
          {i > 0 ? (
            <Tooltip title="Remove">
              <IconButton onClick={() => removeEmail(i)}>
                <RemoveCircleOutlineIcon style={{ color: "red" }} />
              </IconButton>
            </Tooltip>
          ) : i === 0 ? (
            <Tooltip title="Add">
              <IconButton
                onClick={() =>
                  appendEmail({ type: "", paidDate: null, amount: "" })
                }
              >
                <AddCircleOutlineIcon
                  sx={{ color: "green", cursor: "pointer" }}
                />
              </IconButton>
            </Tooltip>
          ) : (
            ""
          )}
        </Grid>
      </>
    );
  };

  return (
    <>
      <Grid item xs={12}>
        <Divider sx={{ my: 0.5 }} />
      </Grid>
      {emailFields.map((item: any, i, arr) => (
        <RenderEmailsItem key={item.id} i={i} arrayLength={arr.length} />
      ))}

      <Grid item xs={12}>
        <Divider sx={{ my: 0.5 }} />
      </Grid>
    </>
  );
};
